'use client';

import { motion } from 'framer-motion';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

export default function GMap({
	heading,
	locations,
	height = 'h-[400px]',
	backgroundColor = 'bg-white',
	isFirst = false,
}: components['schemas']['DynamicZoneGMapComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	// Animation variants
	const fadeIn = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: { duration: 0.6, ease: 'easeOut' },
		},
	};

	return (
		<section
			className={cn(
				'g-map-section px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:px-8',
				backgroundColor,
			)}>
			<noscript>
				<style>{`
					.g-map-section h2[style],
					.g-map-section h3[style],
					.g-map-section p[style],
					.g-map-section div[style*='opacity'],
					.g-map-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				{heading && (
					<SectionHeading className="mb-8 text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
						{heading}
					</SectionHeading>
				)}
				<div className="grid gap-8">
					{locations?.map((map, index) => {
						return (
							<motion.div
								key={index}
								initial="hidden"
								whileInView="visible"
								viewport={{ once: true, margin: '-100px' }}
								variants={fadeIn}
								data-testid="map-container"
								className="flex flex-col gap-4">
								{map.title && (
									<h3 className="text-xl font-semibold text-gray-900">
										{map.title}
									</h3>
								)}
								<div
									className={cn(
										'w-full overflow-hidden rounded-lg shadow-lg',
										height,
									)}>
									<iframe
										src={map.map_embed_url}
										className="h-full w-full border-0"
										allowFullScreen={true}
										loading="lazy"
										referrerPolicy="no-referrer-when-downgrade"
										aria-label={`Google locations location - ${map.title}`}
										title={`Google locations location - ${map.title}`}
									/>
								</div>
							</motion.div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
